import React, { useEffect, useMemo, useState } from "react";
import { Dialog } from "components/dialog";
import { SubscriptionCreateRequest, SubscriptionPlan } from "types";
import { subscriptionPlansApi, subscriptionsApi, LLM_MODEL_MAPPING, EMBEDDING_MODEL_MAPPING } from "api/models";
import { handleAxiosAndCaughtError, validateJson } from "utils";
import toast from "react-hot-toast";
import { Plus } from "lucide-react";
import PureDropDown from "components/pure-dropdown";
import { useTranslation } from "react-i18next";
import { LLM_MODEL_TYPE_CONFIGS, EMBEDDING_MODEL_TYPE_CONFIGS } from "constants/configuration-models";
import LLMModelFormFields, { LLMModelFormData } from "components/llm-model-form-fields/LLMModelFormFields";
import EmbeddingModelFormFields, { EmbeddingModelFormData } from "components/embedding-model-form-fields/EmbeddingModelFormFields";

// Helper function to convert local datetime to UTC datetime that represents the same time
const formatDateForApi = (inputDate: string): string => {
  // Convert local datetime to UTC datetime that represents the same time
  // e.g., "2025-10-17T08:00" becomes "2025-10-17T08:00:00.000Z"
  const date = new Date(inputDate);
  return date.toISOString();
};

interface CreateSubscriptionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const CreateSubscriptionDialog: React.FC<CreateSubscriptionDialogProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [isCreating, setIsCreating] = useState(false);
  const [createForm, setCreateForm] = useState<SubscriptionCreateRequest>({
    name: "",
    start_date: "",
    end_date: "",
    configurations: {
      numOfWorkspaces: 0,
      numOfUsers: 0,
      numOfAgents: 0,
      numOfWorkflows: 0,
      maxStorageSize: 0,
    },
    is_auto_onboard: false,
  });

  const [createFormText, setCreateFormText] = useState({
    configurations: JSON.stringify(
      {
        numOfWorkspaces: 0,
        numOfUsers: 0,
        numOfAgents: 0,
        numOfWorkflows: 0,
        maxStorageSize: 0,
      },
      null,
      2
    ),
  });

  const [jsonErrors, setJsonErrors] = useState({
    configurations: "",
  });

  const [errors, setErrors] = useState({
    name: "",
    start_date: "",
    end_date: "",
    plan: "",
    organization_name: "",
    workspace_name: "",
    admin_name: "",
    admin_email: "",
    admin_password: "",
  });

  const [subscriptionPlanId, setSubscriptionPlanId] = useState<string>("");
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [isLoadingPlans, setIsLoadingPlans] = useState<boolean>(false);

  const [onboardDetails, setOnboardDetails] = useState({
    organization_name: "",
    workspace_name: "",
    admin_name: "",
    admin_email: "",
    admin_password: "",
  });

  const [llmModelConfig, setLlmModelConfig] = useState<LLMModelFormData>({
    name: "",
    type: "Azure OpenAI GPT 4.1",
    configuration: "",
  });

  const [embeddingModelConfig, setEmbeddingModelConfig] = useState<EmbeddingModelFormData>({
    name: "",
    type: "Azure OpenAI Text Embedding 3 Small",
    configuration: "",
  });

  const selectedPlan: SubscriptionPlan | undefined = useMemo(() => {
    return plans.find((p) => p.id === subscriptionPlanId);
  }, [plans, subscriptionPlanId]);

  const isEnterprisePlan = !!selectedPlan?.is_enterprise;
  const nowIsoLocal = useMemo(() => {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, "0");
    const yyyy = d.getFullYear();
    const mm = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    const hh = pad(d.getHours());
    const mi = pad(d.getMinutes());
    return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
  }, []);

  const isEndBeforeStart = useMemo(() => {
    if (!createForm.start_date || !createForm.end_date) return false;
    return (
      new Date(createForm.end_date).getTime() <
      new Date(createForm.start_date).getTime()
    );
  }, [createForm.start_date, createForm.end_date]);

  // Validate auto-onboard required fields
  const isAutoOnboardFieldsValid = useMemo(() => {
    if (!createForm.is_auto_onboard) return true;
    
    const emailRegex = /\S+@\S+\.\S+/;
    const hasValidOnboardFields = 
      onboardDetails.organization_name.trim() !== "" &&
      onboardDetails.workspace_name.trim() !== "" &&
      onboardDetails.admin_name.trim() !== "" &&
      onboardDetails.admin_email.trim() !== "" &&
      emailRegex.test(onboardDetails.admin_email) &&
      onboardDetails.admin_password.trim() !== "" &&
      onboardDetails.admin_password.length >= 8;
    
    // If LLM model name is provided, configuration must be provided and valid JSON
    const hasValidLLMConfig = !llmModelConfig.name || 
      (llmModelConfig.configuration && llmModelConfig.configuration.trim() !== "" && !validateJson(llmModelConfig.configuration));
    
    // If embedding model name is provided, configuration must be provided and valid JSON
    const hasValidEmbeddingConfig = !embeddingModelConfig.name || 
      (embeddingModelConfig.configuration && embeddingModelConfig.configuration.trim() !== "" && !validateJson(embeddingModelConfig.configuration));
    
    return hasValidOnboardFields && hasValidLLMConfig && hasValidEmbeddingConfig;
  }, [createForm.is_auto_onboard, onboardDetails, llmModelConfig, embeddingModelConfig]);
  useEffect(() => {
    if (!isOpen) return;
    const loadPlans = async () => {
      setIsLoadingPlans(true);
      try {
        const response = await subscriptionPlansApi.getListManagement({
          skip: 0,
          take: 100,
          is_active: true,
        });
        setPlans(response.items || []);
      } catch (err) {
        handleAxiosAndCaughtError(err);
      } finally {
        setIsLoadingPlans(false);
      }
    };
    loadPlans();
  }, [isOpen]);

  // Initialize LLM model configuration when auto onboard is checked
  useEffect(() => {
    if (!isOpen) return;
    if (createForm.is_auto_onboard) {
      const modelTypeConfigs = LLM_MODEL_TYPE_CONFIGS;
      const defaultConfig = JSON.stringify(
        modelTypeConfigs["Azure OpenAI GPT 4.1"],
        null,
        2
      );
      // Only initialize if configuration is empty
      setLlmModelConfig((prev) => ({
        name: prev.name || "",
        type: prev.type || "Azure OpenAI GPT 4.1",
        configuration: prev.configuration || defaultConfig,
      }));
    } else {
      setLlmModelConfig({
        name: "",
        type: "Azure OpenAI GPT 4.1",
        configuration: "",
      });
    }
  }, [isOpen, createForm.is_auto_onboard]);

  // Initialize embedding model configuration when auto onboard is checked
  useEffect(() => {
    if (!isOpen) return;
    if (createForm.is_auto_onboard) {
      const modelTypeConfigs = EMBEDDING_MODEL_TYPE_CONFIGS;
      const defaultConfig = JSON.stringify(
        modelTypeConfigs["Azure OpenAI Text Embedding 3 Small"],
        null,
        2
      );
      // Only initialize if configuration is empty
      setEmbeddingModelConfig((prev) => ({
        name: prev.name || "",
        type: prev.type || "Azure OpenAI Text Embedding 3 Small",
        configuration: prev.configuration || defaultConfig,
      }));
    } else {
      setEmbeddingModelConfig({
        name: "",
        type: "Azure OpenAI Text Embedding 3 Small",
        configuration: "",
      });
    }
  }, [isOpen, createForm.is_auto_onboard]);

  // When plan changes, sync configurations text to the plan's configurations
  useEffect(() => {
    if (!selectedPlan) return;
    try {
      const cfgText = JSON.stringify(
        selectedPlan.configurations || {},
        null,
        2
      );
      setCreateFormText((prev) => ({ ...prev, configurations: cfgText }));
      setJsonErrors((prev) => ({
        ...prev,
        configurations: validateJson(cfgText),
      }));
    } catch (_) {
      // leave as is if stringify fails
    }
  }, [selectedPlan]);

  const handleJsonChange = (field: "configurations", value: string) => {
    setCreateFormText((prev) => ({ ...prev, [field]: value }));
    setJsonErrors((prev) => ({ ...prev, [field]: validateJson(value) }));
  };

  // Live-clear field errors when user edits inputs
  useEffect(() => {
    setErrors((prev) => ({
      ...prev,
      name: createForm.name.trim() ? "" : prev.name,
    }));
  }, [createForm.name]);

  useEffect(() => {
    setErrors((prev) => ({
      ...prev,
      start_date: createForm.start_date ? "" : prev.start_date,
    }));
  }, [createForm.start_date]);

  useEffect(() => {
    setErrors((prev) => ({
      ...prev,
      end_date: createForm.end_date && !isEndBeforeStart ? "" : prev.end_date,
    }));
  }, [createForm.end_date, isEndBeforeStart]);

  useEffect(() => {
    setErrors((prev) => ({
      ...prev,
      plan: subscriptionPlanId ? "" : prev.plan,
    }));
  }, [subscriptionPlanId]);

  useEffect(() => {
    if (!createForm.is_auto_onboard) return;
    setErrors((prev) => ({
      ...prev,
      organization_name: onboardDetails.organization_name.trim()
        ? ""
        : prev.organization_name,
    }));
  }, [onboardDetails.organization_name, createForm.is_auto_onboard]);

  useEffect(() => {
    if (!createForm.is_auto_onboard) return;
    setErrors((prev) => ({
      ...prev,
      workspace_name: onboardDetails.workspace_name.trim()
        ? ""
        : prev.workspace_name,
    }));
  }, [onboardDetails.workspace_name, createForm.is_auto_onboard]);

  useEffect(() => {
    if (!createForm.is_auto_onboard) return;
    setErrors((prev) => ({
      ...prev,
      admin_name: onboardDetails.admin_name.trim() ? "" : prev.admin_name,
    }));
  }, [onboardDetails.admin_name, createForm.is_auto_onboard]);

  useEffect(() => {
    if (!createForm.is_auto_onboard) return;
    const emailRegex = /\S+@\S+\.\S+/;
    setErrors((prev) => ({
      ...prev,
      admin_email:
        onboardDetails.admin_email.trim() &&
        emailRegex.test(onboardDetails.admin_email)
          ? ""
          : prev.admin_email,
    }));
  }, [onboardDetails.admin_email, createForm.is_auto_onboard]);

  useEffect(() => {
    if (!createForm.is_auto_onboard) return;
    setErrors((prev) => ({
      ...prev,
      admin_password:
        onboardDetails.admin_password.trim() &&
        onboardDetails.admin_password.length >= 8
          ? ""
          : prev.admin_password,
    }));
  }, [onboardDetails.admin_password, createForm.is_auto_onboard]);

  const handleCreateSave = async () => {
    // Build inline validation errors
    const newErrors = {
      name: !createForm.name.trim()
        ? t("subscriptions.messages.nameRequired")
        : "",
      start_date: !createForm.start_date
        ? t("subscriptions.messages.startDateRequired")
        : "",
      end_date: !createForm.end_date
        ? t("subscriptions.messages.endDateRequired")
        : isEndBeforeStart
        ? t("subscriptions.messages.endDateAfterStart")
        : "",
      plan: !subscriptionPlanId ? t("subscriptions.messages.planRequired") : "",
      organization_name: "",
      workspace_name: "",
      admin_name: "",
      admin_email: "",
      admin_password: "",
    } as typeof errors;

    if (createForm.is_auto_onboard) {
      const emailRegex = /\S+@\S+\.\S+/;
      newErrors.organization_name = !onboardDetails.organization_name.trim()
        ? t("subscriptions.messages.onboardOrgRequired")
        : "";
      newErrors.workspace_name = !onboardDetails.workspace_name.trim()
        ? t("subscriptions.messages.onboardWorkspaceRequired")
        : "";
      newErrors.admin_name = !onboardDetails.admin_name.trim()
        ? t("subscriptions.messages.onboardAdminNameRequired")
        : "";
      newErrors.admin_email = !onboardDetails.admin_email.trim()
        ? t("subscriptions.messages.onboardAdminEmailRequired")
        : !emailRegex.test(onboardDetails.admin_email)
        ? t("subscriptions.messages.onboardAdminEmailInvalid")
        : "";
      newErrors.admin_password = !onboardDetails.admin_password.trim()
        ? t("subscriptions.messages.onboardAdminPasswordRequired")
        : onboardDetails.admin_password.length < 8
        ? t("subscriptions.messages.onboardAdminPasswordMin")
        : "";
    }

    setErrors(newErrors);

    const hasErrors =
      Object.values(newErrors).some(Boolean) ||
      Boolean(jsonErrors.configurations);
    if (hasErrors) return;

    // Parse JSON text to objects
    let parsedConfigurations = {};

    try {
      parsedConfigurations = JSON.parse(createFormText.configurations);
    } catch (e) {
      toast.error(t("subscriptions.messages.invalidConfigurationsJson"));
      return;
    }

    setIsCreating(true);
    try {
      // Prepare LLM model data if provided (same logic as organization settings)
      let llmModelData: { name: string; type: string; model_metadata: Record<string, any> } | undefined;
      if (llmModelConfig.name && llmModelConfig.configuration && llmModelConfig.configuration.trim()) {
        try {
          // Convert display name to enum value (same as organization settings)
          const modelEnum = LLM_MODEL_MAPPING[llmModelConfig.type];
          if (!modelEnum) {
            throw new Error(`Unknown LLM model type: ${llmModelConfig.type}`);
          }

          llmModelData = {
            name: llmModelConfig.name,
            type: modelEnum, // Use the enum key (e.g., "GPT_OSS")
            model_metadata: JSON.parse(llmModelConfig.configuration || "{}")
          };
        } catch (e) {
          if (e instanceof Error && e.message.includes("Unknown LLM model type")) {
            toast.error(e.message);
          } else {
            toast.error(t("subscriptions.messages.invalidLLMModelJson"));
          }
          setIsCreating(false);
          return;
        }
      }

      // Prepare embedding model data if provided (same logic as organization settings)
      let embeddingModelData: { name: string; type: string; model_metadata: Record<string, any> } | undefined;
      if (embeddingModelConfig.name && embeddingModelConfig.configuration && embeddingModelConfig.configuration.trim()) {
        try {
          // Convert display name to enum value (same as organization settings)
          const modelEnum = EMBEDDING_MODEL_MAPPING[embeddingModelConfig.type];
          if (!modelEnum) {
            throw new Error(`Unknown embedding model type: ${embeddingModelConfig.type}`);
          }

          embeddingModelData = {
            name: embeddingModelConfig.name,
            type: modelEnum, // Use the enum key (e.g., "AZURE_TEXT")
            model_metadata: JSON.parse(embeddingModelConfig.configuration || "{}")
          };
        } catch (e) {
          if (e instanceof Error && e.message.includes("Unknown embedding model type")) {
            toast.error(e.message);
          } else {
            toast.error(t("subscriptions.messages.invalidEmbeddingModelJson"));
          }
          setIsCreating(false);
          return;
        }
      }

      const formData = {
        name: createForm.name,
        start_date: formatDateForApi(createForm.start_date),
        end_date: formatDateForApi(createForm.end_date),
        configurations: parsedConfigurations,
        subscription_plan_id: subscriptionPlanId,
        is_auto_onboard: !!createForm.is_auto_onboard,
        ...(createForm.is_auto_onboard ? { 
          onboard_details: {
            organization_name: onboardDetails.organization_name,
            workspace_name: onboardDetails.workspace_name,
            admin_name: onboardDetails.admin_name,
            admin_email: onboardDetails.admin_email,
            admin_password: onboardDetails.admin_password,
            ...(llmModelData ? { llm_model: llmModelData } : {}),
            ...(embeddingModelData ? { embedding_model: embeddingModelData } : {}),
          }
        } : {}),
      };
      await subscriptionsApi.create(formData);
      await onSuccess();
      onClose();
    } catch (err) {
      handleAxiosAndCaughtError(err);
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    // Reset form when closing
    const defaultConfigurations = {
      numOfWorkspaces: 0,
      numOfUsers: 0,
      numOfAgents: 0,
      numOfWorkflows: 0,
      maxStorageSize: 0,
    };

    setCreateForm({
      name: "",
      start_date: "",
      end_date: "",
      configurations: defaultConfigurations,
      is_auto_onboard: false,
    });
    setSubscriptionPlanId("");
    setCreateFormText({
      configurations: JSON.stringify(defaultConfigurations, null, 2),
    });
    setJsonErrors({
      configurations: "",
    });
    setOnboardDetails({
      organization_name: "",
      workspace_name: "",
      admin_name: "",
      admin_email: "",
      admin_password: "",
    });
    setLlmModelConfig({
      name: "",
      type: "Azure OpenAI GPT 4.1",
      configuration: "",
    });
    setEmbeddingModelConfig({
      name: "",
      type: "Azure OpenAI Text Embedding 3 Small",
      configuration: "",
    });
    onClose();
  };

  return (
    <Dialog
      isOpen={isOpen}
      setIsOpen={handleClose}
      outsideClickDisabled={true}
      backdropHandlerDisabled={true}
    >
      <div className="flex min-h-[60vh] w-full max-w-[40rem] flex-col gap-4 sm:gap-6">
        <div className="border-b border-gray-200 pb-3 sm:pb-4">
          <div className="flex items-center gap-2">
            <Plus className="h-5 w-5 text-blue-500" />
            <h2 className="text-lg font-bold text-gray-900 dark:text-white sm:text-xl">
              {t("subscriptions.dialog.createTitle")}
            </h2>
          </div>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 sm:text-sm">
            {t("subscriptions.dialog.createDescription")}
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-200">
              {t("subscriptions.dialog.nameLabel")}
              <span className="ml-1 text-red-500 dark:text-red-400">*</span>
            </label>
            <input
              type="text"
              value={createForm.name}
              onChange={(e) =>
                setCreateForm((prev: SubscriptionCreateRequest) => ({
                  ...prev,
                  name: e.target.value,
                }))
              }
              className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1 dark:border-gray-600 dark:bg-gray-700 dark:text-white ${
                errors.name
                  ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                  : "border-gray-300 focus:border-blue-500 focus:ring-blue-500"
              }`}
              placeholder={t("subscriptions.enterSubscriptionName")}
            />
            {errors.name && (
              <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                {errors.name}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-200">
                {t("subscriptions.dialog.startDateLabel")}
                <span className="ml-1 text-red-500 dark:text-red-400">*</span>
              </label>
              <input
                type="datetime-local"
                value={createForm.start_date}
                onChange={(e) =>
                  setCreateForm((prev: SubscriptionCreateRequest) => ({
                    ...prev,
                    start_date: e.target.value,
                  }))
                }
                min={nowIsoLocal}
                className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1 dark:border-gray-600 dark:bg-gray-700 dark:text-white ${
                  errors.start_date
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                    : "border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                }`}
              />
              {errors.start_date && (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                  {errors.start_date}
                </p>
              )}
            </div>
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-200">
                {t("subscriptions.dialog.endDateLabel")}
                <span className="ml-1 text-red-500 dark:text-red-400">*</span>
              </label>
              <input
                type="datetime-local"
                value={createForm.end_date}
                onChange={(e) =>
                  setCreateForm((prev: SubscriptionCreateRequest) => ({
                    ...prev,
                    end_date: e.target.value,
                  }))
                }
                min={createForm.start_date || nowIsoLocal}
                className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1 dark:border-gray-600 dark:bg-gray-700 dark:text-white ${
                  isEndBeforeStart
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                    : "border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                }`}
              />
              {(errors.end_date || isEndBeforeStart) && (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                  {errors.end_date ||
                    t("subscriptions.messages.endDateAfterStart")}
                </p>
              )}
            </div>
          </div>

          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-200">
              {t("subscriptions.dialog.planLabel")}
              <span className="ml-1 text-red-500 dark:text-red-400">*</span>
            </label>
            <PureDropDown
              placeholder={
                isLoadingPlans
                  ? t("subscriptions.dialog.loadingPlans")
                  : t("subscriptions.dialog.selectPlan")
              }
              options={plans.map((p) => ({ value: p.id, label: p.name }))}
              value={subscriptionPlanId}
              onChange={(val) =>
                setSubscriptionPlanId(typeof val === "string" ? val : "")
              }
              multiple={false}
              showClearButton={false}
            />
            {errors.plan && (
              <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                {errors.plan}
              </p>
            )}
          </div>

          {subscriptionPlanId && (
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-200">
                {t("subscriptions.dialog.configurationsLabel")}
              </label>
              <textarea
                value={createFormText.configurations}
                onChange={(e) =>
                  handleJsonChange("configurations", e.target.value)
                }
                readOnly={!isEnterprisePlan}
                tabIndex={isEnterprisePlan ? 0 : -1}
                className={`mt-1 w-full rounded-lg border px-4 py-3 font-mono text-sm transition-all duration-200 ${
                  !isEnterprisePlan
                    ? "cursor-not-allowed bg-gray-100 text-gray-500 focus:border-gray-300 focus:outline-none focus:ring-0 dark:border-gray-700 dark:bg-gray-800/60 dark:text-gray-400"
                    : "focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:bg-gray-700 dark:text-white"
                } ${
                  jsonErrors.configurations
                    ? "border-red-500 bg-red-50 focus:border-red-500 focus:ring-red-500/20 dark:bg-red-900/10"
                    : "border-gray-300 focus:border-blue-500 dark:border-gray-600"
                }`}
                placeholder={t(
                  "subscriptions.dialog.configurationsPlaceholder"
                )}
                rows={8}
              />
              {jsonErrors.configurations && (
                <div className="mt-2 flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
                  <svg
                    className="h-4 w-4 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  {jsonErrors.configurations}
                </div>
              )}
              <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                {isEnterprisePlan
                  ? t("subscriptions.dialog.configurationsHintEnterprise")
                  : t("subscriptions.dialog.configurationsHintFixed")}
              </div>
            </div>
          )}
          <div className="flex items-center gap-3 pt-2">
            <input
              id="is_auto_onboard"
              type="checkbox"
              checked={!!createForm.is_auto_onboard}
              onChange={(e) =>
                setCreateForm((prev: SubscriptionCreateRequest) => ({
                  ...prev,
                  is_auto_onboard: e.target.checked,
                }))
              }
              className="h-5 w-5 rounded-md border-gray-300 accent-indigo-600 focus:ring-indigo-500"
            />
            <label
              htmlFor="is_auto_onboard"
              className="text-sm font-medium text-gray-700 dark:text-gray-200"
            >
              {t("subscriptions.dialog.autoOnboardLabel")}
            </label>
          </div>

          {createForm.is_auto_onboard && (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-200">
                    {t("subscriptions.dialog.organizationNameLabel")}
                    <span className="ml-1 text-red-500 dark:text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={onboardDetails.organization_name}
                    onChange={(e) =>
                      setOnboardDetails((prev) => ({
                        ...prev,
                        organization_name: e.target.value,
                      }))
                    }
                    className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1 dark:border-gray-600 dark:bg-gray-700 dark:text-white ${
                      errors.organization_name
                        ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                        : "border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    }`}
                    placeholder={t("subscriptions.enterOrganizationName")}
                  />
                  {errors.organization_name && (
                    <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                      {errors.organization_name}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-200">
                    {t("subscriptions.dialog.workspaceNameLabel")}
                    <span className="ml-1 text-red-500 dark:text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={onboardDetails.workspace_name}
                    onChange={(e) =>
                      setOnboardDetails((prev) => ({
                        ...prev,
                        workspace_name: e.target.value,
                      }))
                    }
                    className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1 dark:border-gray-600 dark:bg-gray-700 dark:text-white ${
                      errors.workspace_name
                        ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                        : "border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    }`}
                    placeholder={t("subscriptions.enterWorkspaceName")}
                  />
                  {errors.workspace_name && (
                    <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                      {errors.workspace_name}
                    </p>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-200">
                    {t("subscriptions.dialog.adminNameLabel")}
                    <span className="ml-1 text-red-500 dark:text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={onboardDetails.admin_name}
                    onChange={(e) =>
                      setOnboardDetails((prev) => ({
                        ...prev,
                        admin_name: e.target.value,
                      }))
                    }
                    className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1 dark:border-gray-600 dark:bg-gray-700 dark:text-white ${
                      errors.admin_name
                        ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                        : "border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    }`}
                    placeholder={t("subscriptions.enterAdminName")}
                  />
                  {errors.admin_name && (
                    <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                      {errors.admin_name}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-200">
                    {t("subscriptions.dialog.adminEmailLabel")}
                    <span className="ml-1 text-red-500 dark:text-red-400">*</span>
                  </label>
                  <input
                    type="email"
                    value={onboardDetails.admin_email}
                    onChange={(e) =>
                      setOnboardDetails((prev) => ({
                        ...prev,
                        admin_email: e.target.value,
                      }))
                    }
                    className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1 dark:border-gray-600 dark:bg-gray-700 dark:text-white ${
                      errors.admin_email
                        ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                        : "border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    }`}
                    placeholder={t("subscriptions.enterAdminEmail")}
                  />
                  {errors.admin_email && (
                    <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                      {errors.admin_email}
                    </p>
                  )}
                </div>
              </div>
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-200">
                  {t("subscriptions.dialog.adminPasswordLabel")}
                  <span className="ml-1 text-red-500 dark:text-red-400">*</span>
                </label>
                <input
                  type="password"
                  value={onboardDetails.admin_password}
                  onChange={(e) =>
                    setOnboardDetails((prev) => ({
                      ...prev,
                      admin_password: e.target.value,
                    }))
                  }
                  className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1 dark:border-gray-600 dark:bg-gray-700 dark:text-white ${
                    errors.admin_password
                      ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                      : "border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  }`}
                  placeholder={t("subscriptions.enterAdminPassword")}
                />
                {errors.admin_password && (
                  <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                    {errors.admin_password}
                  </p>
                )}
              </div>
              <div className="border-t border-gray-200 pt-4 dark:border-gray-700">
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
                  <h3 className="mb-4 text-sm font-semibold text-gray-900 dark:text-white">
                    {t("subscriptions.dialog.modelsSection")}
                  </h3>
                  <div className="space-y-6">
                    <div>
                      <h4 className="mb-3 text-xs font-medium text-gray-700 dark:text-gray-300">
                        {t("settings.llmModel")}
                      </h4>
                      <LLMModelFormFields
                        formData={llmModelConfig}
                        onFormDataChange={setLlmModelConfig}
                        showValidation={true}
                      />
                    </div>
                    <div className="border-t border-gray-200 pt-6 dark:border-gray-700">
                      <h4 className="mb-3 text-xs font-medium text-gray-700 dark:text-gray-300">
                        {t("settings.embeddingModel")}
                      </h4>
                      <EmbeddingModelFormFields
                        formData={embeddingModelConfig}
                        onFormDataChange={setEmbeddingModelConfig}
                        showValidation={true}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end pt-3 sm:pt-4">
          <button
            type="button"
            onClick={handleCreateSave}
            disabled={
              isCreating || 
              !createForm.name.trim() || 
              !createForm.start_date || 
              !createForm.end_date || 
              isEndBeforeStart || 
              !subscriptionPlanId || 
              !!jsonErrors.configurations ||
              !isAutoOnboardFieldsValid
            }
            className="flex items-center justify-center gap-2 rounded-lg bg-blue-500 px-6 py-2 font-semibold text-white transition-colors hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-20 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isCreating ? (
              <div className="border-t-transparent h-4 w-4 animate-spin rounded-full border-2 border-white" />
            ) : (
              <Plus size={16} />
            )}
            {t("subscriptions.actions.add")}
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default CreateSubscriptionDialog;
